import { useApolloClient } from '@apollo/client';
import { clearHumioSessionCache } from '@investigator/features/investigations/utils';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import { cloneDeep } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { useTenantInfo, useTokenManagement } from '..';
import { ROUTES } from '../../constants';
import { buildLoginRedirectUrl, isPublicRoute } from '../../utils';
import {
  AuthContext,
  clearUserSpecificData,
  eventBus,
  getUserId,
  getUserScopedKey,
  OrgTenantsContext,
} from '../../providers';
import { AuthState } from '../../types/auth-types';
import {
  clearSSOStorage,
  isRefreshTokenError as checkIsRefreshTokenError,
} from '../../utils/authErrorUtils';

export const SIGN_OUT_HUMIO_SESSION = () => {
  return `
    mutation {
      logoutOfSession
    }
  `;
};

// Keep skipping Amplify-managed prefixes, but allow legacy tokens to be swept
const SKIP_PREFIXES = [
  'CognitoIdentityServiceProvider',
  'amplify-',
  'aws.cognito',
];
const SKIP_KEYS = new Set<string>([]);

// Timeout configurations
const LOGOUT_TIMEOUT_MS = 5000;
const HUMIO_LOGOUT_TIMEOUT_MS = 2000;
const COGNITO_LOGOUT_TIMEOUT_MS = 3000;
const APOLLO_CLEANUP_TIMEOUT_MS = 1000;

const withTimeout = <T>(
  promise: Promise<T>,
  ms: number,
  operation: string,
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error(`${operation} timed out after ${ms}ms`)),
        ms,
      ),
    ),
  ]);
};

// Custom navigate function using window.location
const navigate = (to: string, options?: { replace?: boolean }) => {
  if (options?.replace) {
    window.location.replace(to);
  } else {
    window.location.assign(to);
  }
};

export const useUserSession = () => {
  const { saveIdToken } = useTokenManagement();

  const { user, setAuthState, setUser, authState } = useContext(AuthContext);
  const { humioUrlPrefix } = useTenantInfo();
  const { setSelectedOrgTenants } = useContext(OrgTenantsContext);
  const location = {
    pathname: window.location.pathname,
    search: window.location.search,
  };
  const client = useApolloClient();

  const userId = getUserId(user);

  // Circuit breaker for repeated auth failures
  const authFailureCountRef = useRef(0);
  const lastAuthFailureTimeRef = useRef(0);
  const MAX_AUTH_FAILURES = 3;
  const FAILURE_RESET_WINDOW_MS = 60000; // Reset counter after 1 minute

  const signOutOfHumioSession = useCallback(async (): Promise<boolean> => {
    const userTokenKey = getUserScopedKey('session_id_token', userId);
    const sessionToken =
      localStorage.getItem(userTokenKey) ||
      localStorage.getItem('session_id_token') ||
      '';

    if (!sessionToken) {
      return true;
    }

    const url =
      humioUrlPrefix === 'search-internal'
        ? (process.env.NX_PUBLIC_POV_HUMIO_BASE_HREF ??
          '/search-internal/graphql')
        : humioUrlPrefix.startsWith('search-')
          ? `/${humioUrlPrefix}/graphql`
          : (process.env.NX_PUBLIC_HUMIO_BASE_HREF ?? '/search/graphql');

    try {
      const result = await withTimeout(
        axios({
          method: 'post',
          url,
          data: JSON.stringify({ query: SIGN_OUT_HUMIO_SESSION() }),
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
          timeout: HUMIO_LOGOUT_TIMEOUT_MS,
        }),
        HUMIO_LOGOUT_TIMEOUT_MS,
        'Humio logout',
      );

      return result.data?.data?.logoutOfSession ?? true;
    } catch (error) {
      console.warn('[Humio Logout] Non-critical error:', error);
      return false;
    }
  }, [humioUrlPrefix, userId]);

  const cleanupApolloClient = useCallback(async (): Promise<void> => {
    try {
      client.stop();
      await withTimeout(
        (async () => {
          try {
            await client.clearStore();
          } catch (clearError) {
            console.warn(
              '[Apollo Cleanup] clearStore failed, using cache.reset():',
              clearError,
            );
            client.cache.reset();
          }
        })(),
        APOLLO_CLEANUP_TIMEOUT_MS,
        'Apollo cleanup',
      );
    } catch (error) {
      console.warn('[Apollo Cleanup] Error during cleanup:', error);
      client.cache.reset();
    }
  }, [client]);

  const signOut = useCallback(async () => {
    const hardTimeoutId = setTimeout(() => {
      console.warn('[SignOut] Hard timeout reached, forcing redirect');
      window.location.href = ROUTES.login;
    }, LOGOUT_TIMEOUT_MS);

    let hasRedirected = false;

    try {
      // Clear SSO hints ASAP
      clearSSOStorage();
      sessionStorage.removeItem('eula_check_complete');
      localStorage.removeItem('post_eula_redirect');
      localStorage.removeItem('next');

      const cleanupPromises = [
        // 1) Humio logout (best-effort)
        signOutOfHumioSession().catch((e) =>
          console.warn('[SignOut] Humio logout failed:', e),
        ),

        // 2) Cognito global signout (best-effort with timeout)
        withTimeout(
          Auth.signOut({ global: true }),
          COGNITO_LOGOUT_TIMEOUT_MS,
          'Cognito logout',
        ).catch((e) => {
          console.error('[SignOut] Cognito logout error:', e);
        }),

        // 3) Apollo cleanup (best-effort)
        cleanupApolloClient().catch((e) =>
          console.warn('[SignOut] Apollo cleanup failed:', e),
        ),

        // 4) Local storage cleanup (tokens + user-scoped data)
        Promise.resolve().then(() => {
          if (userId) clearUserSpecificData(userId);
          clearHumioSessionCache(userId);

          const keysToRemove: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) continue;

            // Remove all auth/session artifacts including legacy tokens
            if (
              key.includes('CognitoIdentityServiceProvider') ||
              key.includes('amplify') ||
              key.includes('aws.cognito') ||
              key.includes('docs_id_token') ||
              key.includes('session_id_token') ||
              key.includes('isEulaAccepted')
            ) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => localStorage.removeItem(key));
        }),

        // 5) Notify app
        new Promise<void>((resolve) => {
          eventBus.emit('user-logout');
          setTimeout(resolve, 50);
        }),
      ];

      await Promise.race([
        Promise.allSettled(cleanupPromises),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('Cleanup timeout')),
            LOGOUT_TIMEOUT_MS - 500,
          ),
        ),
      ]);
    } catch (error) {
      console.error('[SignOut] Error during logout:', error);
    } finally {
      clearTimeout(hardTimeoutId);

      if (!hasRedirected) {
        hasRedirected = true;

        try {
          sessionStorage.clear();
          setUser?.(undefined);
          setAuthState?.(AuthState.SignedOut);
          setSelectedOrgTenants?.(undefined);

          if (
            window.pendo?.isReady?.() &&
            typeof window.pendo.stopGuides === 'function'
          ) {
            window.pendo.stopGuides();
          }
        } catch (finalError) {
          console.error('[SignOut] Final cleanup error:', finalError);
        }

        window.location.href = ROUTES.login;
      }
    }
  }, [
    signOutOfHumioSession,
    cleanupApolloClient,
    setAuthState,
    setSelectedOrgTenants,
    setUser,
    userId,
  ]);

  const currentUserSession = useCallback(async (): Promise<
    CognitoUserSession | undefined
  > => {
    try {
      return await Auth.currentSession();
    } catch (err) {
      console.warn('[currentUserSession] Session error:', err);
      return undefined;
    }
  }, []);

  const deregisterUserMFA = useCallback(async () => {
    try {
      await withTimeout(
        Auth.setPreferredMFA(user, 'NOMFA'),
        3000,
        'MFA deregistration',
      );
    } catch (error) {
      console.error('[DeregisterMFA] Failed to deregister MFA:', error);
    }
    await signOut();
  }, [signOut, user]);

  const setUserSessionState = useCallback(
    (authData: CognitoUserSession) => {
      if (!setUser) return;

      const currentIdToken = user?.signInUserSession?.idToken?.jwtToken;
      const newIdToken = authData?.getIdToken()?.getJwtToken();

      // TODO: Create an actual type for this
      type UserDupeType = typeof user & {
        signInUserSession: CognitoUserSession;
      };

      if (newIdToken && currentIdToken !== newIdToken) {
        const userDupe = cloneDeep(user);
        if (userDupe?.signInUserSession) {
          Object.assign(userDupe.signInUserSession, authData);
        }
        setUser(userDupe);
        saveIdToken(newIdToken, userDupe as UserDupeType);
      }
    },
    [saveIdToken, setUser, user],
  );

  const checkUserMFA = useCallback(
    (onMfaRequiredCallback?: () => void) => {
      if (
        user?.preferredMFA === 'NOMFA' &&
        !user?.attributes?.['custom:allow_no_mfa']
      ) {
        if (onMfaRequiredCallback) {
          onMfaRequiredCallback();
        } else {
          navigate('/registration/mfa', {
            replace: true,
          });
        }
      }
    },
    [user?.attributes, user?.preferredMFA],
  );

  // Clear stale keys from *other* users (allow legacy token keys to be purged)
  useEffect(() => {
    if (!userId || !user) return;

    const allKeys = Object.keys(localStorage);
    const otherUserKeys = allKeys.filter((key) => {
      if (SKIP_KEYS.has(key)) return false;
      if (SKIP_PREFIXES.some((p) => key.startsWith(p))) return false;

      const emailPattern = /_([^_]+@[^_]+\.[^_]+)$/;
      const subPattern = /_([a-f0-9-]{36})$/;

      const emailMatch = key.match(emailPattern);
      const subMatch = key.match(subPattern);

      if (emailMatch && emailMatch[1] !== userId) return true;
      if (subMatch && subMatch[1] !== userId) return true;

      return false;
    });

    if (otherUserKeys.length > 0) {
      otherUserKeys.forEach((key) => localStorage.removeItem(key));
    }
  }, [userId, user]);

  // Session check on navigation
  useEffect(() => {
    async function checkUserSession() {
      try {
        if (user && authState === AuthState.SignedIn) {
          // Check if we've exceeded failure threshold
          const now = Date.now();
          if (now - lastAuthFailureTimeRef.current > FAILURE_RESET_WINDOW_MS) {
            // Reset failure counter after time window
            authFailureCountRef.current = 0;
          }

          if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
            console.error(
              '[Session Check] Maximum auth failures reached, forcing sign out',
            );
            await signOut();
            return;
          }

          const userSession = await Auth.currentSession().catch(async (err) => {
            console.warn(
              '[Session Check] Session error, attempting recovery:',
              err,
            );

            try {
              const currentUser = await Auth.currentAuthenticatedUser({
                bypassCache: true,
              });
              if (currentUser) {
                const recovered = await Auth.currentSession();
                // Reset failure count on successful recovery
                authFailureCountRef.current = 0;
                return recovered;
              }
            } catch (recoveryError) {
              console.error('[Session Check] Recovery failed:', recoveryError);

              // Increment failure counter
              authFailureCountRef.current += 1;
              lastAuthFailureTimeRef.current = Date.now();

              // TODO: Create an actual type for this
              type RecoveryError = { message: string };

              const msg =
                typeof recoveryError === 'object' &&
                recoveryError &&
                'message' in recoveryError &&
                typeof (recoveryError as RecoveryError).message === 'string'
                  ? (recoveryError as RecoveryError).message
                  : '';

              const isNetwork = msg.includes('Network Error');
              const onTransition =
                window.location.pathname.includes('loading') ||
                window.location.pathname.includes('registration');

              // Check for refresh token errors using utility function
              const isRefreshToken = checkIsRefreshTokenError(
                recoveryError,
                window.location.pathname,
              );

              if (isRefreshToken) {
                console.error(
                  '[Session Check] Refresh token error detected, signing out immediately',
                );
                authFailureCountRef.current = MAX_AUTH_FAILURES; // Force immediate sign-out
                await signOut();
                return null;
              }

              if (!isNetwork && !onTransition) {
                // Check if we've hit the failure threshold
                if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
                  console.error(
                    '[Session Check] Maximum failures reached, signing out',
                  );
                  await signOut();
                } else {
                  console.warn(
                    `[Session Check] Auth failure ${authFailureCountRef.current}/${MAX_AUTH_FAILURES}, will retry`,
                  );
                }
              }
            }
            return null;
          });

          if (userSession) {
            setUserSessionState(userSession);
            // Reset failure count on successful session check
            authFailureCountRef.current = 0;
          }
        }
      } catch (e) {
        console.error('[Session Check] Unexpected error:', e);
        authFailureCountRef.current += 1;
        lastAuthFailureTimeRef.current = Date.now();

        if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
          console.error(
            '[Session Check] Maximum failures reached, signing out',
          );
          await signOut();
        }
      }
    }

    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Redirect unauthenticated access to /login
  useLayoutEffect(() => {
    if (authState === AuthState.Loading || authState === undefined) return;

    // Skip redirect for public routes (login, SSO, registration, privacy policy)
    if (authState !== AuthState.SignedIn && !isPublicRoute(location.pathname)) {
      navigate(buildLoginRedirectUrl(location.pathname + location.search), {
        replace: true,
      });
    }
  }, [authState, location.pathname, location.search]);

  return {
    signOut,
    currentUserSession,
    setUserSessionState,
    checkUserMFA,
    deregisterUserMFA,
  };
};
